// constants.js

/** -----------------------------------------
 * High-level helpers (optional)
 * ------------------------------------------*/
export const STAGES = {
  PRIMARY: 'الابتدائي',
  MIDDLE: 'المتوسط',
  HIGH: 'الثانوي',
  LANG: 'اللغات',
  COACH: 'Coach',
};

export const HIGH_STREAMS = ['لغات أجنبية', 'آداب', 'علوم'];
export const LANG_MODULES = ['الإنجليزية', 'الفرنسية', 'الإيطالية', 'الألمانية', 'التركية', 'الكورية'];
export const LANG_LEVELS = Array.from({ length: 12 }, (_, i) => i + 1);

/** -----------------------------------------
 * LEVELS  (flat list of all levels used by the app)
 * NOTE: Keep names EXACT — SUBJECTS.level must match
 * ------------------------------------------*/
export const LEVELS = [
  // ========= الابتدائي =========
  // Primary 1–5 (Arabic+Math combined for 1–2)
  '1 ابتدائي',
  '2 ابتدائي',
  '3 ابتدائي',
  '4 ابتدائي',
  '5 ابتدائي',

  // ========= المتوسط =========
  // Middle school 1–4
  '1 متوسط',
  '2 متوسط',
  '3 متوسط',
  '4 متوسط',

  // ========= الثانوي =========
  // First Year
  '1ere annee (جذع مشترك آداب)',
  '1ere annee (جذع مشترك علوم وتكنولوجيا)',
  // Second Year
  '2eme annee (علوم تجريبية)',
  '2eme annee (رياضيات)',
  '2eme annee (تقني رياضي)',
  '2eme annee (تسيير واقتصاد)',
  '2eme annee (آداب وفلسفة)',
  '2eme annee (لغات أجنبية)',
  // BAC / Third Year
  '3eme annee (علوم تجريبية)',
  '3eme annee (رياضيات)',
  '3eme annee (تقني رياضي)',
  '3eme annee (تسيير واقتصاد)',
  '3eme annee (آداب وفلسفة)',
  '3eme annee (لغات أجنبية)',

  // ========= اللغات =========
  // Language programs: we expose one "level label" per language+level
  // (kept here in case you want to reuse the same UI that lists LEVELS)
  ...LANG_MODULES.flatMap(lang =>
    LANG_LEVELS.map(n => `${STAGES.LANG} — ${lang} — المستوى ${n}`)
  ),

  // ========= Coach =========
  `${STAGES.COACH}`,
];

/** -----------------------------------------
 * SUBJECTS
 * Each subject is tied to one "level" string from LEVELS.
 * For الابتدائي 1–2: "العربية + الرياضيات" is ONE module.
 * From 3 ابتدائي and up: Arabic, French, Math are separate.
 * ------------------------------------------*/
export const SUBJECTS = [
  // ========= الابتدائي =========
  // 1 & 2 ابتدائي — one combined module
  { id: 'pri1_ar_math', name: 'العربية + الرياضيات', level: '1 ابتدائي' },
  { id: 'pri2_ar_math', name: 'العربية + الرياضيات', level: '2 ابتدائي' },

  // 3–5 ابتدائي — three modules: (العربية + الرياضيات), الفرنسية, الإنجليزية
  { id: 'pri3_ar_math', name: 'العربية + الرياضيات', level: '3 ابتدائي' },
  { id: 'pri3_fr',      name: 'اللغة الفرنسية',      level: '3 ابتدائي' },
  { id: 'pri3_en',      name: 'اللغة الإنجليزية',     level: '3 ابتدائي' },

  { id: 'pri4_ar_math', name: 'العربية + الرياضيات', level: '4 ابتدائي' },
  { id: 'pri4_fr',      name: 'اللغة الفرنسية',      level: '4 ابتدائي' },
  { id: 'pri4_en',      name: 'اللغة الإنجليزية',     level: '4 ابتدائي' },

  { id: 'pri5_ar_math', name: 'العربية + الرياضيات', level: '5 ابتدائي' },
  { id: 'pri5_fr',      name: 'اللغة الفرنسية',      level: '5 ابتدائي' },
  { id: 'pri5_en',      name: 'اللغة الإنجليزية',     level: '5 ابتدائي' },

  // ========= المتوسط =========
  // Common set across 1–4 متوسط (as per your note)
  // عربية، فرنسية، إنجليزية، علوم طبيعية، فيزياء، رياضيات
  ...['1 متوسط', '2 متوسط', '3 متوسط', '4 متوسط'].flatMap((lvl, idx) => ([
    { id: `mid${idx+1}_ar`,   name: 'اللغة العربية',        level: lvl },
    { id: `mid${idx+1}_fr`,   name: 'اللغة الفرنسية',        level: lvl },
    { id: `mid${idx+1}_en`,   name: 'اللغة الإنجليزية',      level: lvl },
    { id: `mid${idx+1}_bio`,  name: 'علوم الطبيعة والحياة',  level: lvl },
    { id: `mid${idx+1}_phy`,  name: 'الفيزياء',              level: lvl },
    { id: `mid${idx+1}_math`, name: 'الرياضيات',             level: lvl },
  ])),

  // ========= الثانوي =========
  // 1ere annee (جذع مشترك آداب)
  { id: 'hs1a_ar', name: 'اللغة العربية',   level: '1ere annee (جذع مشترك آداب)' },
  { id: 'hs1a_en', name: 'اللغة الإنجليزية', level: '1ere annee (جذع مشترك آداب)' },
  { id: 'hs1a_fr', name: 'اللغة الفرنسية',   level: '1ere annee (جذع مشترك آداب)' },

  // 1ere annee (جذع مشترك علوم وتكنولوجيا)
  { id: 'hs1s_math', name: 'الرياضيات',                level: '1ere annee (جذع مشترك علوم وتكنولوجيا)' },
  { id: 'hs1s_phy',  name: 'الفيزياء',                  level: '1ere annee (جذع مشترك علوم وتكنولوجيا)' },
  { id: 'hs1s_bio',  name: 'علوم الطبيعة والحياة',      level: '1ere annee (جذع مشترك علوم وتكنولوجيا)' },

  // 2eme annee (streamed)
  // علوم تجريبية
  { id: 'hs2exp_math', name: 'الرياضيات',               level: '2eme annee (علوم تجريبية)' },
  { id: 'hs2exp_phy',  name: 'الفيزياء',                 level: '2eme annee (علوم تجريبية)' },
  { id: 'hs2exp_bio',  name: 'علوم الطبيعة والحياة',     level: '2eme annee (علوم تجريبية)' },

  // رياضيات
  { id: 'hs2math_math', name: 'الرياضيات',              level: '2eme annee (رياضيات)' },
  { id: 'hs2math_phy',  name: 'الفيزياء',                level: '2eme annee (رياضيات)' },

  // تقني رياضي
  { id: 'hs2tech_math', name: 'الرياضيات',              level: '2eme annee (تقني رياضي)' },
  { id: 'hs2tech_phy',  name: 'الفيزياء',                level: '2eme annee (تقني رياضي)' },

  // تسيير واقتصاد
  { id: 'hs2mgt_math', name: 'الرياضيات',               level: '2eme annee (تسيير واقتصاد)' },
  { id: 'hs2mgt_acc',  name: 'المحاسبة',                 level: '2eme annee (تسيير واقتصاد)' },

  // آداب وفلسفة
  { id: 'hs2lit_ar',  name: 'اللغة العربية',             level: '2eme annee (آداب وفلسفة)' },
  { id: 'hs2lit_fr',  name: 'اللغة الفرنسية',             level: '2eme annee (آداب وفلسفة)' },
  { id: 'hs2lit_en',  name: 'اللغة الإنجليزية',           level: '2eme annee (آداب وفلسفة)' },
  { id: 'hs2lit_phil',name: 'الفلسفة',                   level: '2eme annee (آداب وفلسفة)' },

  // لغات أجنبية
  { id: 'hs2lang_ar', name: 'اللغة العربية',             level: '2eme annee (لغات أجنبية)' },
  { id: 'hs2lang_fr', name: 'اللغة الفرنسية',             level: '2eme annee (لغات أجنبية)' },
  { id: 'hs2lang_en', name: 'اللغة الإنجليزية',           level: '2eme annee (لغات أجنبية)' },
  { id: 'hs2lang_es', name: 'الإسبانية',                  level: '2eme annee (لغات أجنبية)' },

  // 3eme annee / BAC
  // علوم تجريبية
  { id: 'hs3exp_math', name: 'الرياضيات',               level: '3eme annee (علوم تجريبية)' },
  { id: 'hs3exp_phy',  name: 'الفيزياء',                 level: '3eme annee (علوم تجريبية)' },
  { id: 'hs3exp_bio',  name: 'علوم الطبيعة والحياة',     level: '3eme annee (علوم تجريبية)' },

  // رياضيات
  { id: 'hs3math_math', name: 'الرياضيات',              level: '3eme annee (رياضيات)' },
  { id: 'hs3math_phy',  name: 'الفيزياء',                level: '3eme annee (رياضيات)' },

  // تقني رياضي
  { id: 'hs3tech_math', name: 'الرياضيات',              level: '3eme annee (تقني رياضي)' },
  { id: 'hs3tech_phy',  name: 'الفيزياء',                level: '3eme annee (تقني رياضي)' },

  // تسيير واقتصاد
  { id: 'hs3mgt_math', name: 'الرياضيات',               level: '3eme annee (تسيير واقتصاد)' },
  { id: 'hs3mgt_acc',  name: 'المحاسبة',                 level: '3eme annee (تسيير واقتصاد)' },

  // آداب وفلسفة
  { id: 'hs3lit_ar',  name: 'اللغة العربية',             level: '3eme annee (آداب وفلسفة)' },
  { id: 'hs3lit_fr',  name: 'اللغة الفرنسية',             level: '3eme annee (آداب وفلسفة)' },
  { id: 'hs3lit_en',  name: 'اللغة الإنجليزية',           level: '3eme annee (آداب وفلسفة)' },
  { id: 'hs3lit_phil',name: 'الفلسفة',                   level: '3eme annee (آداب وفلسفة)' },

  // لغات أجنبية
  { id: 'hs3lang_ar', name: 'اللغة العربية',             level: '3eme annee (لغات أجنبية)' },
  { id: 'hs3lang_fr', name: 'اللغة الفرنسية',             level: '3eme annee (لغات أجنبية)' },
  { id: 'hs3lang_en', name: 'اللغة الإنجليزية',           level: '3eme annee (لغات أجنبية)' },
  { id: 'hs3lang_es', name: 'الإسبانية',                  level: '3eme annee (لغات أجنبية)' },

  // ========= اللغات =========
  // Each language level is its own selectable “subject” under a “level” label
  // like: "اللغات — الإنجليزية — المستوى 1"
  ...LANG_MODULES.flatMap((lang) =>
    LANG_LEVELS.map((n) => ({
      id: `lang_${lang}_${n}`,
      name: `${lang} — المستوى ${n}`,
      level: `${STAGES.LANG} — ${lang} — المستوى ${n}`,
    }))
  ),

  // ========= Coach (Nutritionist) =========
  {
    id: 'nutrition_basic',
    name: 'Nutrition',
    level: `${STAGES.COACH}`,
  },
    {
    id: 'english_step_preparation',
    name: 'English (STEP)',
    level: `${STAGES.COACH}`,
  },
];

export const getSubjectsForTutorType = (tutorType) => {
  const isCoach = String(tutorType || 'Teacher').trim().toLowerCase() === 'coach';
  return SUBJECTS.filter((subject) =>
    isCoach ? subject.level === STAGES.COACH : subject.level !== STAGES.COACH
  );
};
